import { useContext, useEffect, useRef, useState } from 'react';

import FormModal from '../../../../components/ui/FormModal';
import { LoyaltyContext } from '../../../../context/loyalty/LoyaltyContext';
import { WalletModel, WalletMyDesign, WalletMyDesignChanges, WalletMyDesignResult, WalletProgram } from '../../../../models/interfaces';
import { WalletImageCropper } from './WalletImageCropper';
import { HERO_GOOGLE_SIZE, isHexColor, MAX_IMAGE_BYTES, readImageSize, STRIP_SIZE, TEMPLATE_URL, toHexColor } from './walletUtils';

const NAME_MAX = 60;
const DESCRIPTION_MAX = 300;
const DEFAULT_BACKGROUND = '#1E3A5F';

type BannerKey = 'strip' | 'heroGoogle';

interface BannerFieldProps {
    label: string;
    size: { width: number; height: number };
    templateUrl: string;
    templateLabel: string;
    currentUrl: string | null;      // banner guardado (al editar)
    file: File | null;
    onFile: (file: File | null) => void;
    invalid: boolean;
    disabled: boolean;
}

// Banner del diseño: si el PNG ya tiene la medida exacta se usa tal cual; si no, se abre el recortador
const BannerField = ({ label, size, templateUrl, templateLabel, currentUrl, file, onFile, invalid, disabled }: BannerFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [toCrop, setToCrop] = useState<File | null>(null);
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!file) { setPendingUrl(null); return; }
        const url = URL.createObjectURL(file);
        setPendingUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const pick = async (selected: File | undefined) => {
        if (inputRef.current) inputRef.current.value = '';
        if (!selected) return;
        const isPng = selected.type === 'image/png' || selected.name.toLowerCase().endsWith('.png');
        if (isPng && selected.size <= MAX_IMAGE_BYTES) {
            try {
                const actual = await readImageSize(selected);
                if (actual.width === size.width && actual.height === size.height) { onFile(selected); return; }
            } catch { /* se intenta con el recortador */ }
        }
        setToCrop(selected);
    };

    const preview = pendingUrl ?? currentUrl;

    return (
        <div className={`border rounded-3 p-3 h-100 d-flex flex-column ${invalid ? 'border-danger' : ''}`}>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <div className="fw-semibold small">{label} *</div>
                <a href={templateUrl} download className="small text-nowrap"><i className="bi bi-download me-1" />{templateLabel}</a>
            </div>
            <div className="small text-muted mb-2">PNG de exactamente {size.width}×{size.height} px, máx. 1 MB. Puede subir cualquier foto y ajustar el encuadre.</div>
            <div className="border rounded bg-light overflow-hidden mb-2 d-flex align-items-center justify-content-center"
                style={{ width: '100%', aspectRatio: `${size.width} / ${size.height}`, maxHeight: 220 }}>
                {preview
                    ? <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <i className="bi bi-image text-muted fs-3" />}
            </div>
            {file && <div className="small text-primary mb-2"><i className="bi bi-arrow-up-circle me-1" />Nueva imagen lista ({(file.size / 1024).toFixed(0)} KB)</div>}
            {invalid && <div className="small text-danger mb-2">Suba el {label.toLowerCase()}.</div>}
            <div className="mt-auto d-flex flex-wrap gap-2">
                <input ref={inputRef} type="file" accept="image/*" className="d-none" disabled={disabled} onChange={(e) => pick(e.target.files?.[0])} />
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    <i className="bi bi-upload me-1" />{preview ? 'Cambiar imagen' : 'Subir imagen'}
                </button>
                {file && (
                    <>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => setToCrop(file)}>
                            <i className="bi bi-crop me-1" />Ajustar
                        </button>
                        <button type="button" className="btn btn-sm btn-link text-muted" disabled={disabled} onClick={() => onFile(null)}>Descartar</button>
                    </>
                )}
            </div>
            {toCrop && (
                <WalletImageCropper source={toCrop} target={size} title={`Encuadrar ${label.toLowerCase()}`}
                    onCancel={() => setToCrop(null)} onDone={(cropped) => { setToCrop(null); onFile(cropped); }} />
            )}
        </div>
    );
};

interface WalletDesignFormProps {
    tipo: WalletProgram;
    design: WalletMyDesign | null;              // null = crear
    baseModels: WalletModel[];                  // modelos de SwiftPay del mismo tipo (para "Basado en")
    banners: { apple?: string; google?: string };  // banners guardados del diseño (al editar)
    onClose: () => void;
    onSaved: (result: WalletMyDesignResult) => void;
    onOpenGuide: (section?: string) => void;
}

// Formulario "Crear diseño" / editar un diseño propio
export const WalletDesignForm = ({ tipo, design, baseModels, banners, onClose, onSaved, onOpenGuide }: WalletDesignFormProps) => {
    const { saveMyDesign } = useContext(LoyaltyContext);
    const editing = !!design;

    const initial = {
        nombre: design?.nombre ?? '',
        descripcion: design?.descripcion ?? '',
        colorFondo: toHexColor(design?.colorFondo) || DEFAULT_BACKGROUND,
        colorTexto: toHexColor(design?.colorTexto),
        colorEtiqueta: toHexColor(design?.colorEtiqueta),
    };

    const [form, setForm] = useState(initial);
    const [basadoEn, setBasadoEn] = useState(editing ? '' : (baseModels.find(m => m.esPredeterminado)?.codigo ?? ''));
    const [files, setFiles] = useState<Record<BannerKey, File | null>>({ strip: null, heroGoogle: null });
    const [usar, setUsar] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(editing && !!(initial.colorTexto || initial.colorEtiqueta));
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (field: keyof typeof initial) => (value: string) => setForm(prev => ({ ...prev, [field]: value }));

    const errors = {
        nombre: !form.nombre.trim() || form.nombre.length > NAME_MAX,
        descripcion: form.descripcion.length > DESCRIPTION_MAX,
        colorFondo: !isHexColor(form.colorFondo),
        colorTexto: !!form.colorTexto && !isHexColor(form.colorTexto),
        colorEtiqueta: !!form.colorEtiqueta && !isHexColor(form.colorEtiqueta),
        strip: !editing && !files.strip,
        heroGoogle: !editing && !files.heroGoogle,
    };
    const invalid = (field: keyof typeof errors) => submitted && errors[field];

    const buildChanges = (): WalletMyDesignChanges => {
        const changes: WalletMyDesignChanges = {};
        if (!editing) changes.tipoPrograma = tipo;
        (['nombre', 'descripcion', 'colorFondo', 'colorTexto', 'colorEtiqueta'] as const).forEach(field => {
            const value = form[field].trim();
            const isColor = field.startsWith('color');
            const normalized = isColor ? value.toUpperCase() : value;
            // Al crear se envía todo lo que tenga valor; al editar, solo lo que cambió
            if (!normalized) return;
            if (editing && normalized === (isColor ? initial[field].toUpperCase() : initial[field].trim())) return;
            changes[field] = normalized;
        });
        if (basadoEn) changes.basadoEn = basadoEn;
        if (files.strip) changes.strip = files.strip;
        if (files.heroGoogle) changes.heroGoogle = files.heroGoogle;
        if (usar) changes.usar = true;
        return changes;
    };

    const handleSave = async () => {
        setSubmitted(true);
        setError('');
        if (Object.values(errors).some(Boolean)) return;

        const changes = buildChanges();
        if (editing && Object.keys(changes).length === 0) { setError('No hay cambios para guardar.'); return; }

        setSaving(true);
        const resp = await saveMyDesign(design?.codigo ?? null, changes);
        setSaving(false);
        if (resp.error || !resp.result) { setError(resp.error ?? 'No se pudo guardar el diseño.'); return; }
        onSaved(resp.result);
    };

    const colorInput = (field: 'colorFondo' | 'colorTexto' | 'colorEtiqueta', label: string, help: string, optional: boolean) => (
        <div className="col-md-4">
            <label className="form-label small text-muted mb-1">{label}{optional ? '' : ' *'}</label>
            <div className="input-group input-group-sm has-validation">
                <input type="color" className="form-control form-control-color" style={{ maxWidth: 44 }} disabled={saving}
                    value={isHexColor(form[field]) ? form[field] : '#000000'} onChange={(e) => set(field)(e.target.value.toUpperCase())} />
                <input className={`form-control ${invalid(field) ? 'is-invalid' : ''}`} maxLength={7} disabled={saving}
                    placeholder={optional ? 'Automático' : '#RRGGBB'} value={form[field]} onChange={(e) => set(field)(e.target.value)} />
                {optional && form[field] && !editing && (
                    <button type="button" className="btn btn-outline-secondary" title="Volver a automático" onClick={() => set(field)('')}><i className="bi bi-x" /></button>
                )}
                <div className="invalid-feedback">Use el formato #RRGGBB.</div>
            </div>
            <div className="form-text">{help}</div>
        </div>
    );

    return (
        <FormModal
            title={editing ? 'Editar diseño' : 'Crear diseño'}
            subtitle={<>
                {tipo === 'sellos' ? 'Sellos' : 'Puntos'}{editing ? ` · ${design!.nombre}` : ''} ·{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onOpenGuide('sp-necesitas')}>
                    <i className="bi bi-question-circle me-1" />¿Cómo crear mi diseño?
                </button>
            </>}
            onClose={onClose}
            onSave={handleSave}
            saving={saving}
            saveLabel="Guardar"
            error={error}
            size="lg"
        >
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="d-flex justify-content-between">
                        <label className="form-label small text-muted mb-1" htmlFor="design-name">Nombre *</label>
                        <span className={`small ${form.nombre.length > NAME_MAX ? 'text-danger' : 'text-muted'}`}>{form.nombre.length}/{NAME_MAX}</span>
                    </div>
                    <input id="design-name" className={`form-control ${invalid('nombre') ? 'is-invalid' : ''}`} maxLength={NAME_MAX} autoFocus
                        placeholder="Navidad 2026" value={form.nombre} disabled={saving} onChange={(e) => set('nombre')(e.target.value)} />
                    <div className="invalid-feedback">El nombre es requerido (máx. {NAME_MAX} caracteres).</div>
                </div>
                <div className="col-md-6">
                    <label className="form-label small text-muted mb-1" htmlFor="design-base">Basado en</label>
                    <select id="design-base" className="form-select" value={basadoEn} disabled={saving} onChange={(e) => setBasadoEn(e.target.value)}>
                        {editing && <option value="">Sin cambios</option>}
                        {!editing && <option value="">Predeterminado</option>}
                        {baseModels.map(model => <option key={model.codigo} value={model.codigo}>{model.nombre}</option>)}
                    </select>
                    <div className="form-text">Su diseño toma de ahí el orden de los datos y los mensajes; los colores y las imágenes son los suyos.</div>
                </div>
                <div className="col-12">
                    <div className="d-flex justify-content-between">
                        <label className="form-label small text-muted mb-1" htmlFor="design-description">Descripción</label>
                        <span className={`small ${form.descripcion.length > DESCRIPTION_MAX ? 'text-danger' : 'text-muted'}`}>{form.descripcion.length}/{DESCRIPTION_MAX}</span>
                    </div>
                    <input id="design-description" className={`form-control ${invalid('descripcion') ? 'is-invalid' : ''}`} maxLength={DESCRIPTION_MAX}
                        placeholder="Rojo y dorado" value={form.descripcion} disabled={saving} onChange={(e) => set('descripcion')(e.target.value)} />
                </div>

                <div className="col-md-6">
                    <BannerField label="Banner iPhone" size={STRIP_SIZE} templateUrl={TEMPLATE_URL.iphone} templateLabel="Descargar plantilla iPhone"
                        currentUrl={banners.apple ?? null} file={files.strip} onFile={(file) => setFiles(prev => ({ ...prev, strip: file }))}
                        invalid={!!invalid('strip')} disabled={saving} />
                </div>
                <div className="col-md-6">
                    <BannerField label="Banner Android" size={HERO_GOOGLE_SIZE} templateUrl={TEMPLATE_URL.android} templateLabel="Descargar plantilla Android"
                        currentUrl={banners.google ?? null} file={files.heroGoogle} onFile={(file) => setFiles(prev => ({ ...prev, heroGoogle: file }))}
                        invalid={!!invalid('heroGoogle')} disabled={saving} />
                </div>
                <div className="col-12 small text-muted">
                    <i className="bi bi-info-circle me-1" />
                    En iPhone el nombre del cliente se escribe sobre el lado izquierdo del banner: déjelo liso. No ponga texto ni su logo en las imágenes.{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onOpenGuide('sp-paso2')}>Ver recomendaciones</button>
                </div>

                {colorInput('colorFondo', 'Color de fondo', 'Se usa en iPhone y Android.', false)}
                <div className="col-12">
                    <button type="button" className="btn btn-sm btn-link text-decoration-none px-0" onClick={() => setShowAdvanced(v => !v)}>
                        <i className={`bi ${showAdvanced ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`} />Avanzado: color de texto y etiquetas
                    </button>
                </div>
                {showAdvanced && (
                    <>
                        {colorInput('colorTexto', 'Color de texto', 'Solo iPhone. Vacío: se calcula para que se lea bien.', true)}
                        {colorInput('colorEtiqueta', 'Color de etiquetas', 'Solo iPhone. Un tono más suave que el texto.', true)}
                    </>
                )}

                {!design?.enUso && (
                    <div className="col-12">
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="design-use" checked={usar} disabled={saving} onChange={(e) => setUsar(e.target.checked)} />
                            <label className="form-check-label" htmlFor="design-use">Usar este diseño</label>
                            <div className="form-text mt-0">Al guardar, la tarjeta de {tipo} pasa a usar este diseño (se quitan los colores y banners que tuviera configurados aparte).</div>
                        </div>
                    </div>
                )}
            </div>
        </FormModal>
    );
};
